import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Container,
  Typography,
  Tabs,
  Tab,
  Button,
  TextField,
  Switch,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  ListItemText,
  OutlinedInput,
  Checkbox,
} from "@mui/material";
import {
  History as HistoryIcon,
} from "@mui/icons-material";
import {
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import Header from "../../Header";
import ioClient from "socket.io-client";
import BeerillionerAudienceModal from "../BeerillionerAudienceModal";
import SpectatorOnOffButton from "../../SpectatorOnOffButton";
import SoundMuteButton from "../../../Common/SoundMuteButton";

import { API_BASE, authHeaders, df } from "./constants";
import SortableQuestionRow from "./SortableQuestionRow";
import BeerillionerConfigTab from "./BeerillionerConfigTab";
import BeerillionerHostTab from "./BeerillionerHostTab";
import BeerillionerHistoryTab from "./BeerillionerHistoryTab";
import useBeerillionerHostSounds from "./useBeerillionerHostSounds";

const BeerillionerDashboard = ({
  initialTab = 0,
  showTabNavigation = true,
  showHeader = true,
}) => {
  const [tab, setTab] = useState(initialTab);
  const [config, setConfig] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [fffStoredQuestions, setFffStoredQuestions] = useState([]);
  const [questionSets, setQuestionSets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saveLoading, setSaveLoading] = useState(false);
  const [gameName, setGameName] = useState("");
  const [fffTimer, setFffTimer] = useState(30);
  const [questionTimer, setQuestionTimer] = useState(0);
  const [audiencePlayEnabled, setAudiencePlayEnabled] = useState(false);
  const [audienceTimer, setAudienceTimer] = useState(15);
  const [audiencePlayStats, setAudiencePlayStats] = useState(null);
  const [audiencePlayPaused, setAudiencePlayPaused] = useState(false);
  const [audiencePlayActive, setAudiencePlayActive] = useState(false);
  const [audiencePlayLocked, setAudiencePlayLocked] = useState(false);
  const [qDialogOpen, setQDialogOpen] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [qForm, setQForm] = useState({
    question_text: "",
    option_a: "",
    option_b: "",
    option_c: "",
    option_d: "",
    correct_answer: "A",
    level: 1,
    set_id: "",
    topic_id: "",
    difficulty_id: "",
  });
  const [qDialogType, setQDialogType] = useState("main");
  const [setDialogOpen, setSetDialogOpen] = useState(false);
  const [newSetName, setNewSetName] = useState("");
  const [viewSetId, setViewSetId] = useState("");
  const [prizeDialogOpen, setPrizeDialogOpen] = useState(false);
  const [editingPrize, setEditingPrize] = useState(null);
  const [prizeForm, setPrizeForm] = useState({
    label: "",
    value_text: "",
    image_url: "",
    is_checkpoint: false,
    topic_ids: [],
    difficulty_ids: [],
  });
  const [session, setSession] = useState(null);
  const [fffMode, setFffMode] = useState("manual");
  const [fffQuestion, setFffQuestion] = useState({
    question_text: "",
    option_a: "",
    option_b: "",
    option_c: "",
    option_d: "",
    correct_answer: "A",
  });
  const [selectedFFFQId, setSelectedFFFQId] = useState("");
  const [fffLeaderboard, setFffLeaderboard] = useState([]);
  const [fffLeaderboardVisible, setFffLeaderboardVisible] = useState(false);
  const [fffRoundEnded, setFffRoundEnded] = useState(null);
  const [fffRound, setFffRound] = useState(1);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [, setOptionsRevealed] = useState(false);
  const [audienceResults, setAudienceResults] = useState(null);
  const [audienceGraphVisible, setAudienceGraphVisible] = useState(false);
  const [audienceLifelinePending, setAudienceLifelinePending] = useState(false);
  const [audienceLifelineReady, setAudienceLifelineReady] = useState(false);
  const [audienceModalOpen, setAudienceModalOpen] = useState(false);
  const [revealResult, setRevealResult] = useState(null);
  const [removedOptions, setRemovedOptions] = useState([]);
  const [revealedOptions, setRevealedOptions] = useState({});
  const [shownOptions, setShownOptions] = useState([]);
  const [activeFFFQuestion, setActiveFFFQuestion] = useState(null);
  const [fffTimerValue, setFffTimerValue] = useState(null);
  const spectatorRef = useRef(null);
  const beerSoundsRef = useRef(null);
  const fffTimerRef = useRef(null);
  const [questionTimerValue, setQuestionTimerValue] = useState(null);
  const questionTimerRef = useRef(null);
  const [gameOverAdmin, setGameOverAdmin] = useState(null);
  const socketRef = useRef(null);
  const [sessionMode, setSessionMode] = useState("preset");
  const [selectedQuestionSetId, setSelectedQuestionSetId] = useState("");
  const [quizSongs, setQuizSongs] = useState([]);
  const [selectedQuizSongId, setSelectedQuizSongId] = useState("");
  const [fffQuizSongs, setFffQuizSongs] = useState([]);
  const [selectedFFFSongId, setSelectedFFFSongId] = useState("");
  const [fffSongSearch, setFffSongSearch] = useState("");
  const [mainSongSearch, setMainSongSearch] = useState("");
  const [clueVisible, setClueVisible] = useState(false);
  const [songNameVisible, setSongNameVisible] = useState(false);
  const [fffPlayerCount, setFffPlayerCount] = useState(0);
  const [fffPlayers, setFffPlayers] = useState([]);
  const [fffHostCountdown, setFffHostCountdown] = useState(0);
  const beerSessionIdRef = useRef(null);
  const beerFffRoundRef = useRef(1);
  beerSessionIdRef.current = session?.id ?? null;
  beerFffRoundRef.current = fffRound;
  const [historySessions, setHistorySessions] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [expandedSession, setExpandedSession] = useState(null);
  const [sessionDetail, setSessionDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  );

  const apiFetch = useCallback(async (url, options = {}) => {
    const res = await fetch(`${API_BASE}${url}`, {
      headers: authHeaders(),
      ...options,
    });
    return res.json();
  }, []);
  const loadConfig = useCallback(async () => {
    const d = await apiFetch("/api/beerillioner/config");
    if (d.success && d.config) {
      const c = d.config;
      setConfig(c);
      setGameName(c.name || "");
      setFffTimer(c.fff_timer_seconds || 30);
      setQuestionTimer(c.question_timer_seconds || 0);
      setAudiencePlayEnabled(!!c.audience_play_enabled);
      setAudienceTimer(c.audience_timer_seconds || 15);
    }
  }, [apiFetch]);
  const loadQuestions = useCallback(
    async (setId) => {
      const d = await apiFetch(
        setId
          ? `/api/beerillioner/questions?set_id=${setId}`
          : "/api/beerillioner/questions",
      );
      if (d.success) setQuestions(d.questions || []);
    },
    [apiFetch],
  );
  const loadFFFQuestions = useCallback(async () => {
    const d = await apiFetch("/api/beerillioner/fff-questions");
    if (d.success) setFffStoredQuestions(d.questions || []);
  }, [apiFetch]);
  const loadQuestionSets = useCallback(async () => {
    const d = await apiFetch("/api/beerillioner/question-sets");
    if (d.success)
      setQuestionSets((d.sets || []).filter((s) => s.set_type === "main"));
  }, [apiFetch]);
  const loadActiveSession = useCallback(async () => {
    const d = await apiFetch("/api/beerillioner/sessions/active");
    if (d.success) {
      setSession(d.session);
      if (d.config) setConfig(d.config);
      if (d.session?.status === "fff" && d.activeFFF?.question) {
        setActiveFFFQuestion({
          ...d.activeFFF.question,
          correct_answer: d.activeFFF.correct_answer,
        });
        if (d.activeFFF.round_number != null) setFffRound(d.activeFFF.round_number);
        setShownOptions(
          Array.isArray(d.activeFFF.shown_options)
            ? d.activeFFF.shown_options
            : [],
        );
        setFffRoundEnded(
          d.activeFFF.ended_at_ms
            ? {
                reason: "timer",
                correct_answer: d.activeFFF.correct_answer || null,
                round_number: d.activeFFF.round_number ?? null,
              }
            : null,
        );
        if (d.activeFFF.ended_at_ms) {
          setFffTimerValue(0);
          if (fffTimerRef.current) clearInterval(fffTimerRef.current);
        } else if (d.activeFFF.started_at_ms && d.activeFFF.timer_seconds) {
          const remaining = Math.max(
            0,
            Math.ceil(
              (d.activeFFF.started_at_ms +
                d.activeFFF.timer_seconds * 1000 -
                Date.now()) /
                1000,
            ),
          );
          setFffTimerValue(remaining);
          if (fffTimerRef.current) clearInterval(fffTimerRef.current);
          if (remaining > 0) {
            fffTimerRef.current = setInterval(() => {
              setFffTimerValue((p) => {
                if (p <= 1) {
                  clearInterval(fffTimerRef.current);
                  return 0;
                }
                return p - 1;
              });
            }, 1000);
          }
        } else {
          setFffTimerValue(null);
        }
      } else {
        setActiveFFFQuestion(null);
      }
      if (d.session) {
        setAudienceLifelinePending(!!d.session.audience_lifeline_pending);
        setAudienceGraphVisible(!!d.session.audience_graph_visible);
        setClueVisible(!!d.session.clue_visible);
        setSongNameVisible(!!d.session.song_name_visible);
        setFffLeaderboardVisible(!!d.session.fff_leaderboard_visible);
        if (d.fffLeaderboard?.all) setFffLeaderboard(d.fffLeaderboard.all);
        if (d.session.audience_votes) {
          try {
            const votes = JSON.parse(d.session.audience_votes);
            if (votes.percentages) {
              setAudienceResults({ percentages: votes.percentages, total_votes: votes.total_votes });
              setAudienceLifelineReady(true);
            }
          } catch {}
        }
      } else {
        setFffLeaderboardVisible(false);
      }
    }
  }, [apiFetch]);
  const loadQuizSongs = useCallback(
    async (level) => {
      const d = await apiFetch(
        `/api/beerillioner/songs-for-level?level=${level}`,
      );
      if (d.success) {
        setQuizSongs(d.songs || []);
        setSelectedQuizSongId("");
      }
    },
    [apiFetch],
  );
  const loadFFFSongs = useCallback(async () => {
    const d = await apiFetch("/api/beerillioner/songs-for-fff");
    if (d.success) setFffQuizSongs(d.songs || []);
  }, [apiFetch]);
  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    const d = await apiFetch("/api/beerillioner/history");
    if (d.success) setHistorySessions(d.sessions || []);
    setHistoryLoading(false);
  }, [apiFetch]);
  const loadSessionDetail = useCallback(
    async (sessionId) => {
      if (expandedSession === sessionId) {
        setExpandedSession(null);
        setSessionDetail(null);
        return;
      }
      setExpandedSession(sessionId);
      setDetailLoading(true);
      setSessionDetail(null);
      const d = await apiFetch(`/api/beerillioner/history/${sessionId}`);
      if (d.success) setSessionDetail(d);
      setDetailLoading(false);
    },
    [apiFetch, expandedSession],
  );

  useEffect(() => {
    (async () => {
      setLoading(true);
      await Promise.all([
        loadConfig(),
        loadQuestions(),
        loadFFFQuestions(),
        loadQuestionSets(),
        loadActiveSession(),
      ]);
      setLoading(false);
    })();
  }, [
    loadConfig,
    loadQuestions,
    loadFFFQuestions,
    loadQuestionSets,
    loadActiveSession,
  ]);
  useEffect(() => {
    if (tab === 2) loadHistory();
  }, [tab, loadHistory]);

  useEffect(() => {
    setTab(initialTab);
  }, [initialTab]);

  const getNextLevel = () => (session?.current_level || 0) + 1;
  useEffect(() => {
    if (
      session?.session_mode === "random" &&
      session?.status === "main_game" &&
      !currentQuestion &&
      !session.current_question_id &&
      !session.current_quiz_question_id
    ) {
      const nl = getNextLevel();
      if (nl <= (config?.prizes || []).length) loadQuizSongs(nl);
    }
  }, [
    session?.session_mode,
    session?.status,
    session?.current_level,
    session?.current_question_id,
    session?.current_quiz_question_id,
    currentQuestion,
    config?.prizes?.length,
    loadQuizSongs,
  ]);

  useEffect(() => {
    if (!session || session.status !== "main_game" || currentQuestion) return;
    const raw = session.current_question_data;
    if (!raw) return;
    try {
      const d = JSON.parse(raw);
      if (!d?.question_text) return;
      setCurrentQuestion({
        id: session.current_quiz_question_id || session.current_question_id,
        question_text: d.question_text,
        option_a: d.option_a,
        option_b: d.option_b,
        option_c: d.option_c,
        question_clue: d.question_clue || null,
        song_name: d.song_name || null,
        option_d: d.option_d,
        correct_answer: d.correct_answer,
        level: session.current_level,
        songs_master_id: d.songs_master_id,
      });
    } catch {
      /* ignore */
    }
  }, [
    session?.id,
    session?.status,
    session?.current_question_data,
    session?.current_quiz_question_id,
    session?.current_question_id,
    session?.current_level,
    currentQuestion,
  ]);

  useEffect(() => {
    if (fffHostCountdown <= 0) return;
    const id = setTimeout(() => setFffHostCountdown((c) => Math.max(0, c - 1)), 1000);
    return () => clearTimeout(id);
  }, [fffHostCountdown]);

  const beerSounds = useBeerillionerHostSounds(session);
  beerSoundsRef.current = beerSounds;

  useEffect(() => {
    const socket = ioClient(API_BASE);
    socketRef.current = socket;
    socket.on("connect", () => {
      socket.emit("beer:join-global");
      socket.emit("beer:request_player_count");
    });
    socket.on("beer:player_count", (d) => {
      setFffPlayerCount(d.count || 0);
      setFffPlayers(d.players || []);
    });
    socket.on("beer:session_created", () => {
      beerSoundsRef.current.onSessionCreated();
    });
    socket.on("beer:fff_intro", () => {
      beerSoundsRef.current.onFffIntro();
      setSession((p) => (p ? { ...p, status: "fff_intro", fff_leaderboard_visible: 0 } : p));
      setFffLeaderboardVisible(false);
    });
    socket.on("beer:fff_started", (d) => {
      beerSoundsRef.current.onFffStarted();
      setFffHostCountdown(3);
      setFffLeaderboardVisible(false);
      setFffLeaderboard([]);
      setFffRoundEnded(null);
      setShownOptions([]);
      setFffTimerValue(null);
      if (fffTimerRef.current) clearInterval(fffTimerRef.current);
      if (d?.round_number != null) setFffRound(d.round_number);
      if (d?.question) {
        setActiveFFFQuestion({
          ...d.question,
          correct_answer: d.correct_answer,
        });
      }
      socket.emit("beer:request_player_count");
    });
    socket.on("beer:all_options_shown", (d) => {
      if (Array.isArray(d?.shown)) setShownOptions(d.shown);
      beerSoundsRef.current.onAllOptionsShown();
    });
    socket.on("beer:fff_round_ended", (d) => {
      beerSoundsRef.current.onFffRoundEnded();
      if (fffTimerRef.current) clearInterval(fffTimerRef.current);
      setFffTimerValue(0);
      setFffRoundEnded({
        reason: d?.reason || "timer",
        correct_answer: d?.correct_answer || null,
        round_number: d?.round_number ?? null,
      });
    });
    socket.on("beer:fff_leaderboard_toggled", (d) => {
      beerSoundsRef.current.onFffLeaderboardToggled(!!d?.visible);
      setFffLeaderboardVisible(!!d?.visible);
      if (d?.all) setFffLeaderboard(d.all);
    });
    socket.on("beer:hot_seat_selected", () => {
      beerSoundsRef.current.onHotSeatSelected();
    });
    socket.on("beer:question_pushed", () => {
      beerSoundsRef.current.onQuestionPushed();
    });
    socket.on("beer:answer_highlighted", () => {
      beerSoundsRef.current.onAnswerHighlighted();
    });
    socket.on("beer:fff_answer_received", (d) => {
      if (!d) return;
      const curSid = beerSessionIdRef.current;
      if (curSid == null) return;
      if (Number(d.session_id) !== Number(curSid)) return;
      if (
        d.round_number != null &&
        d.round_number !== beerFffRoundRef.current
      )
        return;
      const row = d.entry;
      if (!row) return;
      const key = String(row.device_id ?? "");
      setFffLeaderboard((prev) => {
        const idx = prev.findIndex((e) => String(e.device_id) === key);
        if (idx >= 0) {
          const next = [...prev];
          next[idx] = row;
          return next;
        }
        return [...prev, row];
      });
    });
    socket.on("beer:audience_lifeline_ready", (d) => {
      setAudienceResults(d);
      setAudienceLifelinePending(false);
      setAudienceLifelineReady(true);
    });
    socket.on("beer:audience_graph_toggled", (d) => {
      setAudienceGraphVisible(d.visible);
    });
    socket.on("beer:option_shown", (d) => {
      setShownOptions((prev) =>
        prev.includes(d.option) ? prev : [...prev, d.option],
      );
      beerSoundsRef.current.onOptionShown();
    });
    socket.on("beer:option_hidden", (d) => {
      setShownOptions((prev) => prev.filter((x) => x !== d.option));
    });
    socket.on("beer:option_revealed", (d) => {
      setRevealedOptions((prev) => ({ ...prev, [d.option]: d.is_correct }));
    });
    socket.on("beer:timer_started", (d) => {
      if (d.context === "fff") {
        beerSoundsRef.current.onFffTimerStarted();
        setFffTimerValue(d.seconds);
        if (fffTimerRef.current) clearInterval(fffTimerRef.current);
        fffTimerRef.current = setInterval(() => {
          setFffTimerValue((p) => {
            if (p <= 1) {
              clearInterval(fffTimerRef.current);
              return 0;
            }
            return p - 1;
          });
        }, 1000);
      } else {
        setQuestionTimerValue(d.seconds);
        if (questionTimerRef.current) clearInterval(questionTimerRef.current);
        questionTimerRef.current = setInterval(() => {
          setQuestionTimerValue((p) => {
            if (p <= 1) {
              clearInterval(questionTimerRef.current);
              return 0;
            }
            return p - 1;
          });
        }, 1000);
      }
    });
    socket.on("beer:answer_revealed", (d) => {
      beerSoundsRef.current.onAnswerRevealed(!!d?.is_correct);
      if (questionTimerRef.current) clearInterval(questionTimerRef.current);
      setQuestionTimerValue(null);
      setRevealResult(d);
    });
    socket.on("beer:game_over", (d) => {
      beerSoundsRef.current.onGameOver(d?.reason);
      if (questionTimerRef.current) clearInterval(questionTimerRef.current);
      setQuestionTimerValue(null);
      setGameOverAdmin(d);
    });
    socket.on("beer:lifeline_used", (d) => {
      if (d.type === "50_50" && d.result?.removed)
        setRemovedOptions(d.result.removed);
      if (d.type === "ask_audience") {
        setAudienceGraphVisible(false);
        if (d.result?.pending) { setAudienceLifelinePending(true); setAudienceLifelineReady(false); setAudienceResults(null); }
        else if (d.result?.percentages) { setAudienceResults({ percentages: d.result.percentages, total_votes: d.result.total_votes }); setAudienceLifelinePending(false); setAudienceLifelineReady(true); }
      }
      const ext = parseInt(d.timer_extension_seconds, 10) || 0;
      if (ext > 0) {
        setQuestionTimerValue((p) => (p != null ? p + ext : p));
      }
    });
    socket.on("beer:audience_play_started", () => {
      setAudiencePlayActive(true);
      setAudiencePlayPaused(false);
      setAudiencePlayLocked(false);
      setAudiencePlayStats(null);
    });
    socket.on("beer:audience_play_paused", () => {
      setAudiencePlayPaused(true);
      setAudiencePlayActive(false);
    });
    socket.on("beer:audience_play_resumed", () => {
      setAudiencePlayPaused(false);
      setAudiencePlayActive(true);
    });
    socket.on("beer:audience_answers_locked", (d) => {
      setAudiencePlayLocked(true);
      setAudiencePlayActive(false);
      setAudiencePlayPaused(false);
      setAudiencePlayStats(d);
    });
    socket.on("beer:audience_play_update", (d) => {
      setAudiencePlayStats(d);
    });
    return () => {
      socket.emit("beer:leave-global");
      socket.disconnect();
    };
  }, [loadActiveSession]);

  const saveGeneralConfig = async () => {
    setSaveLoading(true);
    await apiFetch("/api/beerillioner/config", {
      method: "PUT",
      body: JSON.stringify({
        name: gameName,
        fff_timer_seconds: fffTimer,
        question_timer_seconds: questionTimer,
        audience_play_enabled: audiencePlayEnabled,
        audience_timer_seconds: audienceTimer,
      }),
    });
    await loadConfig();
    setSaveLoading(false);
  };
  const savePrize = async (prize) => {
    await apiFetch(`/api/beerillioner/prizes/${prize.id}`, {
      method: "PUT",
      body: JSON.stringify({
        label: prize.label,
        value_text: prize.value_text,
        image_url: prize.image_url,
        is_checkpoint: prize.is_checkpoint,
        topic_ids: prize.topic_ids || [],
        difficulty_ids: prize.difficulty_ids || [],
      }),
    });
    await loadConfig();
  };
  const handleAddPrize = async () => {
    await apiFetch("/api/beerillioner/prizes", {
      method: "POST",
      body: JSON.stringify({}),
    });
    await loadConfig();
  };
  const handleDeletePrize = async (id) => {
    await apiFetch(`/api/beerillioner/prizes/${id}`, { method: "DELETE" });
    await loadConfig();
  };
  const openEditPrize = (prize) => {
    setEditingPrize(prize);
    setPrizeForm({
      label: prize.label || "",
      value_text: prize.value_text || "",
      image_url: prize.image_url || "",
      is_checkpoint: !!prize.is_checkpoint,
      topic_ids: prize.topic_ids || [],
      difficulty_ids: prize.difficulty_ids || [],
    });
    setPrizeDialogOpen(true);
  };
  const savePrizeFromModal = async () => {
    if (!editingPrize) return;
    await savePrize({
      ...editingPrize,
      ...prizeForm,
      is_checkpoint: prizeForm.is_checkpoint ? 1 : 0,
    });
    setPrizeDialogOpen(false);
    setEditingPrize(null);
  };
  const saveLifeline = async (ll) => {
    await apiFetch(`/api/beerillioner/lifelines/${ll.id}`, {
      method: "PUT",
      body: JSON.stringify({
        label: ll.label,
        icon: ll.icon,
        is_enabled: ll.is_enabled,
        extra_timer_seconds: Math.max(0, parseInt(ll.extra_timer_seconds, 10) || 0),
      }),
    });
    await loadConfig();
  };
  const handleCreateSet = async () => {
    if (!newSetName.trim()) return;
    await apiFetch("/api/beerillioner/question-sets", {
      method: "POST",
      body: JSON.stringify({ name: newSetName, set_type: "main" }),
    });
    setNewSetName("");
    setSetDialogOpen(false);
    await loadQuestionSets();
  };
  const handleDeleteSet = async (id) => {
    await apiFetch(`/api/beerillioner/question-sets/${id}`, {
      method: "DELETE",
    });
    await loadQuestionSets();
    await loadQuestions();
  };

  const openAddQ = (type = "main") => {
    setQDialogType(type);
    setEditingQuestion(null);
    setQForm({
      question_text: "",
      option_a: "",
      option_b: "",
      option_c: "",
      option_d: "",
      correct_answer: "A",
      level: 1,
      set_id: type === "main" ? viewSetId || "" : "",
      topic_id: "",
      difficulty_id: "",
    });
    setQDialogOpen(true);
  };
  const openEditQ = (q, type = "main") => {
    setQDialogType(type);
    setEditingQuestion(q);
    setQForm({
      question_text: q.question_text,
      option_a: q.option_a,
      option_b: q.option_b,
      option_c: q.option_c,
      option_d: q.option_d,
      correct_answer: q.correct_answer,
      level: q.level || 1,
      set_id: q.set_id || "",
      topic_id: q.topic_id || "",
      difficulty_id: q.difficulty_id || "",
    });
    setQDialogOpen(true);
  };
  const saveQuestion = async () => {
    const body = {
      ...qForm,
      set_id: qForm.set_id || null,
      topic_id: qForm.topic_id || null,
      difficulty_id: qForm.difficulty_id || null,
    };
    if (qDialogType === "fff") {
      if (editingQuestion)
        await apiFetch(
          `/api/beerillioner/fff-questions/${editingQuestion.id}`,
          { method: "PUT", body: JSON.stringify(body) },
        );
      else
        await apiFetch("/api/beerillioner/fff-questions", {
          method: "POST",
          body: JSON.stringify(body),
        });
      await loadFFFQuestions();
    } else {
      if (editingQuestion)
        await apiFetch(`/api/beerillioner/questions/${editingQuestion.id}`, {
          method: "PUT",
          body: JSON.stringify(body),
        });
      else
        await apiFetch("/api/beerillioner/questions", {
          method: "POST",
          body: JSON.stringify(body),
        });
      await loadQuestions(viewSetId || undefined);
    }
    setQDialogOpen(false);
  };
  const deleteQuestion = async (id) => {
    await apiFetch(`/api/beerillioner/questions/${id}`, { method: "DELETE" });
    await loadQuestions(viewSetId || undefined);
  };
  const deleteFFFQ = async (id) => {
    await apiFetch(`/api/beerillioner/fff-questions/${id}`, {
      method: "DELETE",
    });
    await loadFFFQuestions();
  };
  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oi = questions.findIndex((q) => q.id === active.id);
    const ni = questions.findIndex((q) => q.id === over.id);
    const r = arrayMove(questions, oi, ni);
    setQuestions(r);
    await apiFetch("/api/beerillioner/questions/reorder", {
      method: "POST",
      body: JSON.stringify({ ordered_ids: r.map((q) => q.id) }),
    });
  };

  const createNewSession = async () => {
    // Stop Intro Sound immediately so it cannot overlap New Game cues while the API runs.
    beerSoundsRef.current?.stopBgMusic?.();
    const d = await apiFetch("/api/beerillioner/sessions", {
      method: "POST",
      body: JSON.stringify({
        question_set_id:
          sessionMode === "preset" ? selectedQuestionSetId || null : null,
        session_mode: sessionMode,
      }),
    });
    if (d.success) {
      setSession(d.session);
      setRevealResult(null);
      setCurrentQuestion(null);
      setOptionsRevealed(false);
      setFffLeaderboard([]);
      setRemovedOptions([]);
      setRevealedOptions({});
      setShownOptions([]);
      setActiveFFFQuestion(null);
      setQuestionTimerValue(null);
      setMainSongSearch("");
      setFffSongSearch("");
      setSelectedFFFSongId("");
      setGameOverAdmin(null);
      if (questionTimerRef.current) clearInterval(questionTimerRef.current);
      spectatorRef.current?.ensureOn(d.session?.id || null);
    }
  };
  const startFFF = async () => {
    setFffLeaderboard([]);
    setFffLeaderboardVisible(false);
    setFffRoundEnded(null);
    setRevealResult(null);
    setShownOptions([]);
    setActiveFFFQuestion(null);
    setFffTimerValue(null);
    if (fffTimerRef.current) clearInterval(fffTimerRef.current);
    let body;
    if (fffMode === "random") {
      body = {
        quiz_random: true,
        quiz_song_id: selectedFFFSongId ? parseInt(selectedFFFSongId) : null,
      };
    } else if (fffMode === "stored") {
      if (!selectedFFFQId) return;
      body = { fff_question_id: selectedFFFQId };
    } else {
      if (!fffQuestion.question_text) return;
      body = fffQuestion;
    }
    const d = await apiFetch(`/api/beerillioner/sessions/${session.id}/fff`, {
      method: "POST",
      body: JSON.stringify(body),
    });
    if (d.success) {
      setFffRound(d.round_number);
      setFffHostCountdown(3);
      if (d.question) setActiveFFFQuestion(d.question);
      await loadActiveSession();
      if (d.question) setActiveFFFQuestion(d.question);
      setShownOptions([]);
      setFffTimerValue(null);
      if (fffTimerRef.current) clearInterval(fffTimerRef.current);
    }
  };
  const startFFFIntro = async () => {
    if (!session) return;
    const d = await apiFetch(`/api/beerillioner/sessions/${session.id}/fff/intro`, {
      method: "POST",
    });
    if (d.success) {
      setSession((p) => (p ? { ...p, status: "fff_intro" } : p));
      setFffLeaderboardVisible(false);
    }
  };
  const loadFFFLeaderboard = async (roundOverride) => {
    if (!session) return;
    const rn = roundOverride ?? beerFffRoundRef.current ?? fffRound;
    const d = await apiFetch(
      `/api/beerillioner/sessions/${session.id}/fff/leaderboard?round_number=${rn}`,
    );
    if (d.success) setFffLeaderboard(d.all || []);
  };
  const toggleFFFLeaderboard = async (visible) => {
    if (!session) return;
    if (visible && fffTimerValue !== 0) return;
    const d = await apiFetch(
      `/api/beerillioner/sessions/${session.id}/fff/leaderboard/toggle`,
      {
        method: "POST",
        body: JSON.stringify({ visible: !!visible }),
      },
    );
    if (d.success) {
      setFffLeaderboardVisible(!!d.visible);
      if (d.all) setFffLeaderboard(d.all);
    }
  };
  const selectHotSeat = async (player) => {
    if (!session) return;
    await apiFetch(`/api/beerillioner/sessions/${session.id}/hot-seat`, {
      method: "POST",
      body: JSON.stringify({
        player_id: player.device_id || "",
        display_name: player.display_name,
      }),
    });
    setFffLeaderboardVisible(false);
    await loadActiveSession();
  };

  const resumeAudiencePlay = async () => {
    if (!session) return;
    await apiFetch(
      `/api/beerillioner/sessions/${session.id}/audience-play/resume`,
      { method: "POST" },
    );
  };
  const resetQuestionState = () => {
    setRevealResult(null);
    setAudienceResults(null);
    setAudienceGraphVisible(false);
    setAudienceLifelinePending(false);
    setAudienceLifelineReady(false);
    setOptionsRevealed(false);
    setRemovedOptions([]);
    setRevealedOptions({});
    setShownOptions([]);
    setQuestionTimerValue(null);
    setMainSongSearch("");
    if (questionTimerRef.current) clearInterval(questionTimerRef.current);
    setAudiencePlayActive(false);
    setAudiencePlayPaused(false);
    setAudiencePlayLocked(false);
    setAudiencePlayStats(null);
    setClueVisible(false);
    setSongNameVisible(false);
  };
  const pushQuestionToGame = async (qId) => {
    if (!session) return;
    resetQuestionState();
    const d = await apiFetch(
      `/api/beerillioner/sessions/${session.id}/push-question`,
      { method: "POST", body: JSON.stringify({ question_id: qId }) },
    );
    if (d.success) {
      setCurrentQuestion(d.question);
      await loadActiveSession();
    }
  };
  const pushQuizQuestion = async (songId = null) => {
    if (!session) return;
    resetQuestionState();
    const d = await apiFetch(
      `/api/beerillioner/sessions/${session.id}/push-quiz-question`,
      {
        method: "POST",
        body: JSON.stringify(songId ? { song_id: songId } : {}),
      },
    );
    if (d.success) {
      setCurrentQuestion(d.question);
      await loadActiveSession();
    } else {
      alert(d.error || "No quiz question available");
    }
  };
  const handleNextQ = async () => {
    if (!session) return;
    await apiFetch(`/api/beerillioner/sessions/${session.id}/next-question`, {
      method: "POST",
    });
    setCurrentQuestion(null);
    resetQuestionState();
    setSession((prev) =>
      prev
        ? {
            ...prev,
            current_question_id: null,
            current_quiz_question_id: null,
            current_question_data: null,
            highlighted_answer: null,
            options_revealed: 0,
          }
        : prev,
    );
  };
  const handleNextQuizSameSong = async () => {
    if (!session) return;
    const songId = currentQuestion?.songs_master_id;
    if (!songId) {
      alert("No song is linked to this question.");
      return;
    }
    await handleNextQ();
    await pushQuizQuestion(songId);
  };
  const handleNextQuizNewSong = async () => {
    if (!session) return;
    await handleNextQ();
    setSelectedQuizSongId("");
  };
  const toggleClueVisibility = async () => {
    if (!session) return;
    const newVal = !clueVisible;
    const d = await apiFetch(
      `/api/beerillioner/sessions/${session.id}/toggle-clue`,
      { method: "POST", body: JSON.stringify({ visible: newVal }) },
    );
    if (d.success) setClueVisible(newVal);
  };
  const toggleSongNameVisibility = async () => {
    if (!session) return;
    const newVal = !songNameVisible;
    const d = await apiFetch(
      `/api/beerillioner/sessions/${session.id}/toggle-song-name`,
      { method: "POST", body: JSON.stringify({ visible: newVal }) },
    );
    if (d.success) setSongNameVisible(newVal);
  };
  const highlightAnswer = async (ans) => {
    await apiFetch(`/api/beerillioner/sessions/${session.id}/highlight`, {
      method: "POST",
      body: JSON.stringify({ answer: ans }),
    });
    await loadActiveSession();
  };
  const doShowOption = async (opt) => {
    if (!session) return;
    const d = await apiFetch(
      `/api/beerillioner/sessions/${session.id}/show-option`,
      { method: "POST", body: JSON.stringify({ option: opt }) },
    );
    if (d.success)
      setShownOptions((prev) => (prev.includes(opt) ? prev : [...prev, opt]));
  };
  const doStartFFFTimer = async () => {
    if (!session) return;
    if (fffTimerValue != null) return;
    const seconds = config?.fff_timer_seconds || 30;
    await apiFetch(`/api/beerillioner/sessions/${session.id}/start-timer`, {
      method: "POST",
      body: JSON.stringify({ seconds, context: "fff" }),
    });
    setFffTimerValue(seconds);
    if (fffTimerRef.current) clearInterval(fffTimerRef.current);
    fffTimerRef.current = setInterval(() => {
      setFffTimerValue((p) => {
        if (p <= 1) {
          clearInterval(fffTimerRef.current);
          return 0;
        }
        return p - 1;
      });
    }, 1000);
  };
  const doShowAllOptions = async () => {
    if (!session) return;
    const d = await apiFetch(
      `/api/beerillioner/sessions/${session.id}/show-all-options`,
      { method: "POST", body: JSON.stringify({}) },
    );
    if (d.success && Array.isArray(d.shown)) {
      setShownOptions((prev) => {
        const next = [...prev];
        d.shown.forEach((opt) => {
          if (!next.includes(opt)) next.push(opt);
        });
        return next;
      });
    }
    if (d.success && session.status === "fff" && fffTimerValue == null) {
      await doStartFFFTimer();
    }
  };
  const doHideOption = async (opt) => {
    if (!session) return;
    const d = await apiFetch(
      `/api/beerillioner/sessions/${session.id}/hide-option`,
      { method: "POST", body: JSON.stringify({ option: opt }) },
    );
    if (d.success)
      setShownOptions((prev) => prev.filter((x) => x !== opt));
  };
  const doStartTimer = async () => {
    if (!session) return;
    const seconds = config?.question_timer_seconds || 30;
    await apiFetch(`/api/beerillioner/sessions/${session.id}/start-timer`, {
      method: "POST",
      body: JSON.stringify({ seconds }),
    });
    if (config?.audience_play_enabled) {
      await apiFetch(
        `/api/beerillioner/sessions/${session.id}/audience-play/start`,
        { method: "POST" },
      );
    }
    setQuestionTimerValue(seconds);
    if (questionTimerRef.current) clearInterval(questionTimerRef.current);
    const sid = session.id;
    questionTimerRef.current = setInterval(() => {
      setQuestionTimerValue((p) => {
        if (p <= 1) {
          clearInterval(questionTimerRef.current);
          apiFetch(`/api/beerillioner/sessions/${sid}/timer-expired`, {
            method: "POST",
          }).then((d) => {
            if (d.success) {
              setGameOverAdmin({
                reason: "timer",
                winning_level: d.prize_won?.level || 0,
                prize_won: d.prize_won || null,
                correct_answer: d.correct_answer || null,
              });
            }
          });
          return 0;
        }
        return p - 1;
      });
    }, 1000);
  };
  const doRevealAnswer = async () => {
    if (!session) return;
    if (questionTimerRef.current) clearInterval(questionTimerRef.current);
    setQuestionTimerValue(null);
    const d = await apiFetch(
      `/api/beerillioner/sessions/${session.id}/reveal`,
      { method: "POST" },
    );
    if (!d.success) return;
    setRevealResult({
      is_correct: d.is_correct,
      correct_answer: d.correct_answer,
      highlighted_answer: d.highlighted_answer,
    });
    if (!d.is_correct) {
      const level = session.current_level || 0;
      let checkpointPrize = null;
      for (let i = level - 1; i >= 1; i--) {
        const p = (config?.prizes || []).find(
          (pr) => pr.level === i && pr.is_checkpoint,
        );
        if (p) {
          checkpointPrize = p;
          break;
        }
      }
      setGameOverAdmin({
        reason: "wrong",
        correct_answer: d.correct_answer,
        winning_level: checkpointPrize?.level || 0,
        prize_won: checkpointPrize || null,
      });
    }
  };
  const doWalkAway = async () => {
    if (!session) return;
    const d = await apiFetch(
      `/api/beerillioner/sessions/${session.id}/walk-away`,
      { method: "POST" },
    );
    if (d.success) {
      if (questionTimerRef.current) clearInterval(questionTimerRef.current);
      setQuestionTimerValue(null);
      setGameOverAdmin({
        reason: "walkaway",
        winning_level: d.winning_level ?? Math.max(0, (session.current_level || 0) - 1),
        prize_won: d.prize_won || null,
      });
    }
  };
  const activateLifeline = async (type) => {
    const d = await apiFetch(
      `/api/beerillioner/sessions/${session.id}/lifeline`,
      { method: "POST", body: JSON.stringify({ type }) },
    );
    if (d.success) {
      if (d.type === "50_50" && d.result?.removed)
        setRemovedOptions(d.result.removed);
      if (d.type === "ask_audience") {
        if (d.result?.pending) { setAudienceLifelinePending(true); setAudienceLifelineReady(false); }
        else if (d.result?.percentages) { setAudienceResults({ percentages: d.result.percentages, total_votes: d.result.total_votes }); setAudienceLifelinePending(false); setAudienceLifelineReady(true); }
      }
      await loadActiveSession();
    }
  };
  const toggleAudienceGraph = async (visible) => {
    const d = await apiFetch(
      `/api/beerillioner/sessions/${session.id}/audience-graph/toggle`,
      { method: "POST", body: JSON.stringify({ visible }) },
    );
    if (d.success) {
      setAudienceGraphVisible(d.visible);
    }
  };
  const endGame = async () => {
    await apiFetch(`/api/beerillioner/sessions/${session.id}/end`, {
      method: "POST",
    });
    setSession(null);
    setCurrentQuestion(null);
    setRevealResult(null);
    setGameOverAdmin(null);
  };

  if (loading)
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography color="#aaa">Loading...</Typography>
      </Box>
    );
  const prizes = config?.prizes || [];
  const lifelines = config?.lifelines || [];
  const sessionLifelines = session?.lifelines_remaining
    ? JSON.parse(session.lifelines_remaining)
    : [];
  const maxLevel = prizes.length;
  const isRandom = session?.session_mode === "random";
  const activeTab = showTabNavigation ? tab : initialTab;

  return (
    <>
      {showHeader && <Header />}
      <Container
        maxWidth="xl"
        sx={{ mt: 2, mb: 4 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            mb: 2,
            flexWrap: "wrap",
          }}>
          <Typography
            variant="h4"
            sx={{ color: "#ffd700", fontWeight: 700 }}>
            Who Wants to Beer Millionaire
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <SoundMuteButton />
            <SpectatorOnOffButton
              ref={spectatorRef}
              gameType="beerillioner"
              gameLabel="Who Wants to Beer Millionaire"
              sessionId={session?.id || null}
            />
          </Box>
        </Box>
        {showTabNavigation && (
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            sx={{
              mb: 2,
              "& .MuiTab-root": { color: "#aaa" },
              "& .Mui-selected": { color: "#ffd700" },
            }}>
            <Tab label="Config" />
            <Tab label="Host" />
            <Tab
              label="History"
              icon={<HistoryIcon />}
              iconPosition="start"
            />
          </Tabs>
        )}

        {/* ═══════ CONFIG ═══════ */}
        {activeTab === 0 && (
          <BeerillionerConfigTab
            gameName={gameName}
            setGameName={setGameName}
            fffTimer={fffTimer}
            setFffTimer={setFffTimer}
            questionTimer={questionTimer}
            setQuestionTimer={setQuestionTimer}
            audiencePlayEnabled={audiencePlayEnabled}
            setAudiencePlayEnabled={setAudiencePlayEnabled}
            audienceTimer={audienceTimer}
            setAudienceTimer={setAudienceTimer}
            saveGeneralConfig={saveGeneralConfig}
            saveLoading={saveLoading}
            prizes={prizes}
            handleAddPrize={handleAddPrize}
            openEditPrize={openEditPrize}
            handleDeletePrize={handleDeletePrize}
            config={config}
            API_BASE={API_BASE}
            lifelines={lifelines}
            setConfig={setConfig}
            saveLifeline={saveLifeline}
            df={df}
            setSetDialogOpen={setSetDialogOpen}
            openAddQ={openAddQ}
            viewSetId={viewSetId}
            setViewSetId={setViewSetId}
            loadQuestions={loadQuestions}
            questionSets={questionSets}
            handleDeleteSet={handleDeleteSet}
            questions={questions}
            sensors={sensors}
            closestCenter={closestCenter}
            handleDragEnd={handleDragEnd}
            verticalListSortingStrategy={verticalListSortingStrategy}
            SortableQuestionRow={SortableQuestionRow}
            openEditQ={openEditQ}
            deleteQuestion={deleteQuestion}
            fffStoredQuestions={fffStoredQuestions}
            deleteFFFQ={deleteFFFQ}
          />
        )}

        {/* ═══════ HOST ═══════ */}
        {activeTab === 1 && (
          <BeerillionerHostTab
            session={session}
            sessionMode={sessionMode}
            setSessionMode={setSessionMode}
            setSession={setSession}
            questionSets={questionSets}
            questions={questions}
            selectedQuestionSetId={selectedQuestionSetId}
            setSelectedQuestionSetId={setSelectedQuestionSetId}
            createNewSession={createNewSession}
            playIntroSound={beerSounds.playIntroSound}
            API_BASE={API_BASE}
            config={config}
            lifelines={lifelines}
            activateLifeline={activateLifeline}
            isRandom={isRandom}
            prizes={prizes}
            maxLevel={maxLevel}
            getNextLevel={getNextLevel}
            currentQuestion={currentQuestion}
            setCurrentQuestion={setCurrentQuestion}
            revealResult={revealResult}
            setRevealResult={setRevealResult}
            shownOptions={shownOptions}
            removedOptions={removedOptions}
            revealedOptions={revealedOptions}
            audienceResults={audienceResults}
            audienceGraphVisible={audienceGraphVisible}
            audienceLifelinePending={audienceLifelinePending}
            audienceLifelineReady={audienceLifelineReady}
            clueVisible={clueVisible}
            songNameVisible={songNameVisible}
            quizSongs={quizSongs}
            selectedQuizSongId={selectedQuizSongId}
            selectedFFFSongId={selectedFFFSongId}
            fffQuizSongs={fffQuizSongs}
            fffMode={fffMode}
            setFffMode={setFffMode}
            selectedFFFQId={selectedFFFQId}
            setSelectedFFFQId={setSelectedFFFQId}
            fffStoredQuestions={fffStoredQuestions}
            fffQuestion={fffQuestion}
            setFffQuestion={setFffQuestion}
            fffRound={fffRound}
            activeFFFQuestion={activeFFFQuestion}
            fffTimerValue={fffTimerValue}
            fffLeaderboard={fffLeaderboard}
            fffLeaderboardVisible={fffLeaderboardVisible}
            fffRoundEnded={fffRoundEnded}
            toggleFFFLeaderboard={toggleFFFLeaderboard}
            fffPlayerCount={fffPlayerCount}
            fffPlayers={fffPlayers}
            fffHostCountdown={fffHostCountdown}
            questionTimerValue={questionTimerValue}
            gameOverAdmin={gameOverAdmin}
            setGameOverAdmin={setGameOverAdmin}
            sessionLifelines={sessionLifelines}
            audiencePlayActive={audiencePlayActive}
            audiencePlayPaused={audiencePlayPaused}
            audiencePlayLocked={audiencePlayLocked}
            audiencePlayStats={audiencePlayStats}
            mainSongSearch={mainSongSearch}
            setMainSongSearch={setMainSongSearch}
            fffSongSearch={fffSongSearch}
            setFffSongSearch={setFffSongSearch}
            df={df}
            startFFFIntro={startFFFIntro}
            startFFF={startFFF}
            loadFFFSongs={loadFFFSongs}
            loadFFFLeaderboard={loadFFFLeaderboard}
            doShowOption={doShowOption}
            doShowAllOptions={doShowAllOptions}
            doHideOption={doHideOption}
            doStartFFFTimer={doStartFFFTimer}
            highlightAnswer={highlightAnswer}
            doStartTimer={doStartTimer}
            doRevealAnswer={doRevealAnswer}
            doWalkAway={doWalkAway}
            loadQuizSongs={loadQuizSongs}
            pushQuizQuestion={pushQuizQuestion}
            pushQuestionToGame={pushQuestionToGame}
            selectHotSeat={selectHotSeat}
            handleNextQuizSameSong={handleNextQuizSameSong}
            handleNextQuizNewSong={handleNextQuizNewSong}
            handleNextQ={handleNextQ}
            toggleAudienceGraph={toggleAudienceGraph}
            toggleClueVisibility={toggleClueVisibility}
            toggleSongNameVisibility={toggleSongNameVisibility}
            resumeAudiencePlay={resumeAudiencePlay}
            endGame={endGame}
            setAudienceModalOpen={setAudienceModalOpen}
            setSelectedQuizSongId={setSelectedQuizSongId}
            setSelectedFFFSongId={setSelectedFFFSongId}
          />
        )}

        {/* ═══════ HISTORY ═══════ */}
        {activeTab === 2 && (
          <BeerillionerHistoryTab
            loadHistory={loadHistory}
            historyLoading={historyLoading}
            historySessions={historySessions}
            expandedSession={expandedSession}
            loadSessionDetail={loadSessionDetail}
            detailLoading={detailLoading}
            sessionDetail={sessionDetail}
          />
        )}

        {/* Question Dialog */}
        <Dialog
          open={qDialogOpen}
          onClose={() => setQDialogOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { bgcolor: "#1a1a1a", color: "#fff" } }}>
          <DialogTitle sx={{ color: "#ffd700" }}>
            {editingQuestion ? "Edit" : "Add"}{" "}
            {qDialogType === "fff" ? "FFF " : ""}Question
          </DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Question"
              value={qForm.question_text}
              onChange={(e) =>
                setQForm((p) => ({ ...p, question_text: e.target.value }))
              }
              sx={{ mt: 1, mb: 2, ...df }}
            />
            <Grid
              container
              spacing={1}>
              {["A", "B", "C", "D"].map((o) => (
                <Grid
                  item
                  xs={6}
                  key={o}>
                  <TextField
                    fullWidth
                    label={`Option ${o}`}
                    value={qForm[`option_${o.toLowerCase()}`]}
                    onChange={(e) =>
                      setQForm((p) => ({
                        ...p,
                        [`option_${o.toLowerCase()}`]: e.target.value,
                      }))
                    }
                    sx={df}
                  />
                </Grid>
              ))}
            </Grid>
            <Box sx={{ display: "flex", gap: 2, mt: 2, flexWrap: "wrap" }}>
              <FormControl sx={{ minWidth: 100 }}>
                <InputLabel sx={{ color: "#aaa" }}>Correct</InputLabel>
                <Select
                  value={qForm.correct_answer}
                  onChange={(e) =>
                    setQForm((p) => ({ ...p, correct_answer: e.target.value }))
                  }
                  label="Correct"
                  sx={{ color: "#fff", "& fieldset": { borderColor: "#555" } }}>
                  {["A", "B", "C", "D"].map((o) => (
                    <MenuItem
                      key={o}
                      value={o}>
                      {o}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {qDialogType === "main" && (
                <>
                  <FormControl sx={{ minWidth: 120 }}>
                    <InputLabel sx={{ color: "#aaa" }}>Level</InputLabel>
                    <Select
                      value={qForm.level}
                      onChange={(e) =>
                        setQForm((p) => ({ ...p, level: e.target.value }))
                      }
                      label="Level"
                      sx={{
                        color: "#fff",
                        "& fieldset": { borderColor: "#555" },
                      }}>
                      {prizes.map((p) => (
                        <MenuItem
                          key={p.level}
                          value={p.level}>
                          {p.level} - {p.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl sx={{ minWidth: 130 }}>
                    <InputLabel sx={{ color: "#aaa" }}>Set</InputLabel>
                    <Select
                      value={qForm.set_id}
                      onChange={(e) =>
                        setQForm((p) => ({ ...p, set_id: e.target.value }))
                      }
                      label="Set"
                      sx={{
                        color: "#fff",
                        "& fieldset": { borderColor: "#555" },
                      }}>
                      <MenuItem value="">No Set</MenuItem>
                      {questionSets.map((s) => (
                        <MenuItem
                          key={s.id}
                          value={s.id}>
                          {s.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl sx={{ minWidth: 140 }}>
                    <InputLabel sx={{ color: "#aaa" }}>Topic</InputLabel>
                    <Select
                      value={qForm.topic_id || ""}
                      onChange={(e) =>
                        setQForm((p) => ({ ...p, topic_id: e.target.value }))
                      }
                      label="Topic"
                      sx={{
                        color: "#fff",
                        "& fieldset": { borderColor: "#555" },
                      }}>
                      <MenuItem value="">
                        <em>Any</em>
                      </MenuItem>
                      {(config?.topics || []).map((t) => (
                        <MenuItem
                          key={t.id}
                          value={t.id}>
                          {t.display_name || t.topic_name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl sx={{ minWidth: 130 }}>
                    <InputLabel sx={{ color: "#aaa" }}>Difficulty</InputLabel>
                    <Select
                      value={qForm.difficulty_id || ""}
                      onChange={(e) =>
                        setQForm((p) => ({
                          ...p,
                          difficulty_id: e.target.value,
                        }))
                      }
                      label="Difficulty"
                      sx={{
                        color: "#fff",
                        "& fieldset": { borderColor: "#555" },
                      }}>
                      <MenuItem value="">
                        <em>Any</em>
                      </MenuItem>
                      {(config?.difficulties || []).map((d) => (
                        <MenuItem
                          key={d.id}
                          value={d.id}>
                          {d.level_name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setQDialogOpen(false)}
              sx={{ color: "#aaa" }}>
              Cancel
            </Button>
            <Button
              onClick={saveQuestion}
              variant="contained"
              sx={{ bgcolor: "#ffd700", color: "#000" }}>
              Save
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={setDialogOpen}
          onClose={() => setSetDialogOpen(false)}
          maxWidth="xs"
          fullWidth
          PaperProps={{ sx: { bgcolor: "#1a1a1a", color: "#fff" } }}>
          <DialogTitle sx={{ color: "#ffd700" }}>
            Create Question Set
          </DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              label="Set Name"
              value={newSetName}
              onChange={(e) => setNewSetName(e.target.value)}
              sx={{ mt: 1, ...df }}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleCreateSet();
              }}
            />
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setSetDialogOpen(false)}
              sx={{ color: "#aaa" }}>
              Cancel
            </Button>
            <Button
              onClick={handleCreateSet}
              variant="contained"
              sx={{ bgcolor: "#ffd700", color: "#000" }}>
              Create
            </Button>
          </DialogActions>
        </Dialog>

        {/* Prize Ladder Edit Modal */}
        <Dialog
          open={prizeDialogOpen}
          onClose={() => {
            setPrizeDialogOpen(false);
            setEditingPrize(null);
          }}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { bgcolor: "#1a1a1a", color: "#fff" } }}>
          <DialogTitle sx={{ color: "#ffd700" }}>
            Edit Prize Level {editingPrize?.level}
          </DialogTitle>
          <DialogContent>
            <Grid
              container
              spacing={2}
              sx={{ mt: 0.5 }}>
              <Grid
                item
                xs={12}
                sm={6}>
                <TextField
                  fullWidth
                  label="Label"
                  value={prizeForm.label}
                  onChange={(e) =>
                    setPrizeForm((p) => ({ ...p, label: e.target.value }))
                  }
                  sx={df}
                />
              </Grid>
              <Grid
                item
                xs={12}
                sm={6}>
                <TextField
                  fullWidth
                  label="Value"
                  value={prizeForm.value_text}
                  onChange={(e) =>
                    setPrizeForm((p) => ({ ...p, value_text: e.target.value }))
                  }
                  sx={df}
                />
              </Grid>
              <Grid
                item
                xs={12}>
                <TextField
                  fullWidth
                  label="Image URL"
                  value={prizeForm.image_url}
                  onChange={(e) =>
                    setPrizeForm((p) => ({ ...p, image_url: e.target.value }))
                  }
                  sx={df}
                />
              </Grid>
              {prizeForm.image_url && (
                <Grid
                  item
                  xs={12}>
                  <Box
                    component="img"
                    src={
                      prizeForm.image_url.startsWith("http")
                        ? prizeForm.image_url
                        : `${API_BASE}/${prizeForm.image_url}`
                    }
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: 1,
                      objectFit: "cover",
                      border: "1px solid #333",
                    }}
                    onError={(e) => {
                      e.target.style.display = "none";
                    }}
                  />
                </Grid>
              )}
              <Grid
                item
                xs={12}>
                <FormControl
                  fullWidth
                  size="small">
                  <InputLabel sx={{ color: "#aaa" }}>Topic</InputLabel>
                  <Select
                    multiple
                    displayEmpty
                    value={prizeForm.topic_ids}
                    onChange={(e) => {
                      const v = e.target.value;
                      setPrizeForm((p) => ({
                        ...p,
                        topic_ids: typeof v === "string" ? [] : v,
                      }));
                    }}
                    input={
                      <OutlinedInput
                        label="Topic"
                        sx={{
                          color: "#fff",
                          "& fieldset": { borderColor: "#555" },
                        }}
                      />
                    }
                    renderValue={(sel) =>
                      sel.length === 0
                        ? "Any"
                        : sel
                            .map((id) => {
                              const t = (config?.topics || []).find(
                                (x) => x.id === id,
                              );
                              return t?.display_name || t?.topic_name;
                            })
                            .filter(Boolean)
                            .join(", ") || "Any"
                    }>
                    {(config?.topics || []).map((t) => (
                      <MenuItem
                        key={t.id}
                        value={t.id}>
                        <Checkbox
                          checked={prizeForm.topic_ids.indexOf(t.id) > -1}
                          sx={{
                            color: "#ffd700",
                            "&.Mui-checked": { color: "#ffd700" },
                          }}
                        />
                        <ListItemText
                          primary={t.display_name || t.topic_name}
                        />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid
                item
                xs={12}>
                <FormControl
                  fullWidth
                  size="small">
                  <InputLabel sx={{ color: "#aaa" }}>Difficulty</InputLabel>
                  <Select
                    multiple
                    displayEmpty
                    value={prizeForm.difficulty_ids}
                    onChange={(e) => {
                      const v = e.target.value;
                      setPrizeForm((p) => ({
                        ...p,
                        difficulty_ids: typeof v === "string" ? [] : v,
                      }));
                    }}
                    input={
                      <OutlinedInput
                        label="Difficulty"
                        sx={{
                          color: "#fff",
                          "& fieldset": { borderColor: "#555" },
                        }}
                      />
                    }
                    renderValue={(sel) =>
                      sel.length === 0
                        ? "Any"
                        : sel
                            .map(
                              (id) =>
                                (config?.difficulties || []).find(
                                  (d) => d.id === id,
                                )?.level_name,
                            )
                            .filter(Boolean)
                            .join(", ") || "Any"
                    }>
                    {(config?.difficulties || []).map((d) => (
                      <MenuItem
                        key={d.id}
                        value={d.id}>
                        <Checkbox
                          checked={prizeForm.difficulty_ids.indexOf(d.id) > -1}
                          sx={{
                            color: "#ffd700",
                            "&.Mui-checked": { color: "#ffd700" },
                          }}
                        />
                        <ListItemText primary={d.level_name} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid
                item
                xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!prizeForm.is_checkpoint}
                      onChange={(e) =>
                        setPrizeForm((p) => ({
                          ...p,
                          is_checkpoint: e.target.checked,
                        }))
                      }
                      sx={{
                        color: "#ffd700",
                        "& .MuiSwitch-switchBase.Mui-checked": {
                          color: "#ffd700",
                        },
                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                          { bgcolor: "#ffd700" },
                      }}
                    />
                  }
                  label={
                    <Typography sx={{ color: "#aaa" }}>
                      Checkpoint (CP)
                    </Typography>
                  }
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setPrizeDialogOpen(false);
                setEditingPrize(null);
              }}
              sx={{ color: "#aaa" }}>
              Cancel
            </Button>
            <Button
              onClick={savePrizeFromModal}
              variant="contained"
              sx={{ bgcolor: "#ffd700", color: "#000" }}>
              Save
            </Button>
          </DialogActions>
        </Dialog>
      </Container>

      {/* Audience Results Modal */}
      <BeerillionerAudienceModal
        open={audienceModalOpen}
        onClose={() => setAudienceModalOpen(false)}
        sessionId={session?.id}
      />
    </>
  );
};


export default BeerillionerDashboard;
